/**
 * Program Management Component
 * Allows sponsors to view, edit, and delete their programs
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { ProgramSetupWizard } from './ProgramSetupWizard';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Calendar,
  Users,
  FileText,
  Target
} from 'lucide-react';

interface Program {
  id: number;
  name: string;
  program_type: string;
  description: string | null;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
  capacity: number | null;
  funding_source: string | null;
  program_manager_name: string | null;
  program_manager_email: string | null;
  program_manager_phone: string | null;
  current_enrollment: number;
  created_at: string;
}

interface ProgramManagementProps {
  sponsorId: number;
  cboId: number;
}

export const ProgramManagement: React.FC<ProgramManagementProps> = ({
  sponsorId,
  cboId
}) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingDraftId, setEditingDraftId] = useState<number | null>(null);

  useEffect(() => {
    fetchPrograms();
  }, [cboId]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(
        getWeaveinApiUrl(`/api/programs?cbo_id=${cboId}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId.toString()
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Sort by created_at descending (newest first)
        const sorted = data.sort((a: Program, b: Program) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setPrograms(sorted);
      } else {
        throw new Error('Failed to fetch programs');
      }
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch programs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (programId: number, programName: string) => {
    if (!confirm(`Are you sure you want to delete "${programName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingId(programId);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(
        getWeaveinApiUrl(`/api/programs/${programId}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId.toString()
          }
        }
      );

      if (response.ok) {
        // Remove from list
        setPrograms(programs.filter(p => p.id !== programId));
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete program');
      }
    } catch (err) {
      console.error('Error deleting program:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete program');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (program: Program) => {
    try {
      const token = localStorage.getItem('auth_token');

      const response = await fetch(
        getWeaveinApiUrl(`/api/programs/${program.id}`),
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId.toString()
          },
          body: JSON.stringify({ is_active: !program.is_active })
        }
      );

      if (response.ok) {
        // Update in list
        setPrograms(programs.map(p =>
          p.id === program.id ? { ...p, is_active: !p.is_active } : p
        ));
      } else {
        throw new Error('Failed to update program status');
      }
    } catch (err) {
      console.error('Error updating program:', err);
      alert(err instanceof Error ? err.message : 'Failed to update program');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="text-red-600">Error: {error}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('sponsors.your_programs', 'Your Programs')}</h3>
          <p className="text-sm text-gray-600 mt-1">
            {t('sponsors.manage_your_enrollment_programs', 'Manage your enrollment programs')}
          </p>
        </div>
        <Badge variant="outline">{programs.length} Programs</Badge>
      </div>

      {programs.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-300">{t('sponsors.no_programs_found', 'No programs found')}</p>
          <p className="text-sm text-gray-500 mt-1">
            {t('sponsors.click_create_program_to_get', 'Click "Create Program" to get started')}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {programs.map((program) => (
            <Card key={program.id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {program.name}
                    </h4>
                    <Badge
                      variant={program.is_active ? "default" : "outline"}
                      className={program.is_active ? "bg-green-100 text-green-800" : ""}
                    >
                      {program.is_active ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> {t('sponsors.active', 'Active')}</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> {t('sponsors.draft', 'Draft')}</>
                      )}
                    </Badge>
                    <Badge variant="outline">
                      {program.program_type.replace(/_/g, ' ')}
                    </Badge>
                  </div>

                  {program.description && (
                    <p className="text-sm text-gray-600 mb-3">{program.description}</p>
                  )}

                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    {program.current_enrollment !== undefined && (
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{program.current_enrollment} enrolled</span>
                      </div>
                    )}
                    {program.capacity && (
                      <div className="flex items-center gap-1">
                        <Target className="w-4 h-4" />
                        <span>Capacity: {program.capacity}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      <span>Created {fmt.short(program.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(program)}
                    title={program.is_active ? "Deactivate program" : "Activate program"}
                  >
                    {program.is_active ? (
                      <XCircle className="w-4 h-4" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Always use the program wizard for editing
                      setEditingDraftId(program.id);
                    }}
                    title={t('sponsors.edit_program', 'Edit program')}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(program.id, program.name)}
                    disabled={deletingId === program.id}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title={t('sponsors.delete_program', 'Delete program')}
                  >
                    {deletingId === program.id ? (
                      <LoadingSpinner />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Wizard for editing programs - rendered via Portal to escape stacking context */}
      {editingDraftId && createPortal(
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[99999] overflow-auto p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-auto relative">
            <ProgramSetupWizard
              sponsorId={sponsorId}
              editProgramId={editingDraftId}
              onComplete={(programId) => {
                setEditingDraftId(null);
                fetchPrograms(); // Refresh the list
              }}
              onCancel={() => {
                setEditingDraftId(null);
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProgramManagement;
